"use client";

import { usePathname } from "next/navigation";

/* Wraps marketing chrome that should NOT mount on /admin or /embed.
 *
 * During SSR, usePathname() returns null so the guard is skipped and
 * children render (matching the initial client render where pathname
 * is also null). After hydration, usePathname() resolves to the real
 * path — if it's /admin or /embed, the component returns null and
 * React unmounts the children (no effects run, no paint). This is a
 * normal post-hydration state update, not a hydration mismatch. */
export function HideOnAdmin({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  if (pathname === null) return <>{children}</>;
  if (pathname.startsWith("/admin") || pathname.startsWith("/embed")) return null;
  /* The Forge homepage (/) and shared builds (/b/*) are full-bleed and carry
   * their own header, footer and background; the global chrome stays off
   * so nothing competes with the prompt or costs first-paint time. */
  if (pathname === "/" || pathname.startsWith("/b/") || pathname.startsWith("/w/") || pathname.startsWith("/website-gallery/")) return null;
  return <>{children}</>;
}

/* Same wrapper but for client-side gating — returns the admin-friendly
 * alternative on /admin/*, the marketing version everywhere else. Use
 * for wrappers (SmoothScroll, etc.) where we still need the children to
 * render, just without the heavy parent. */
export function AdminAwareWrap({
  admin,
  marketing,
}: {
  admin: React.ReactNode;
  marketing: React.ReactNode;
}) {
  const pathname = usePathname();
  if (pathname?.startsWith("/admin")) return <>{admin}</>;
  return <>{marketing}</>;
}

/* Hide on wall-art PDP routes (/digital-art/<slug>).
 *
 * Brian "no fake" + duplicate-detection sweep 2026-05-31 PM: the
 * full-page similarity audit on the populated cluster-bomb subset
 * measured 0.887 raw / 0.082 unique-only at 30 PDPs. The 0.082
 * confirms per-product content quality but the 0.887 reflects 93%
 * boilerplate share — global chrome (ReviewsMarquee, marquee
 * banners, sticky CTAs, etc.) repeats verbatim on every PDP and
 * volume-wise drowns the per-product signal.
 *
 * Modern Google's content-block shingling discounts repeated chrome
 * (so the 0.887 isn't what their dedupe actually scores against),
 * BUT audit panels that use naive Copyscape-style tools see the
 * 0.887 directly. Hiding the heaviest chrome on the 79k-PDP
 * surface specifically gives both Google's smart dedupe AND naive
 * audit tools acceptable numbers without restructuring the global
 * marketing chrome on every other surface.
 *
 * Wrap heavy chrome (ReviewsMarquee, etc.) with this on
 * marketing pages where we want them on the homepage/listings but
 * NOT on the per-product detail pages.
 *
 * Matches /digital-art/<something> exactly — NOT /digital-art (the
 * listing page, where the marquee is valuable for once-per-session
 * trust signal). */
export function HideOnPDP({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  if (pathname === null) return <>{children}</>;
  if (/^\/digital-art\/[^/]+/.test(pathname)) return null;
  return <>{children}</>;
}
